import { Alert, flash, Label, Modal, SubmitButton } from "@xola/ui-kit";
import {
    DateFormat,
    formatDate,
    handleSuccessfulSubmission,
    isAllDay,
    isNonCanceledExperienceItem,
    isOtherSellerPurchase,
    isPackageItem,
    isPartnerPackagePurchase,
    PurchasePriceStrategy,
    SwitchField,
} from "common";
import { isEmpty, isNumber } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Arrival,
    ArrivalError,
    Event,
    Experience,
    ExperiencePurchaseItem,
    FeatureRole,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseRuleFilterError,
    ValidationLevel,
} from "types";
import { ConfirmLeaveModal } from "../Common/ConfirmLeaveModal";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ArrivalPicker } from "../DatePicker/ArrivalPicker";
import { Loader } from "../Loader";
import { AddOnLimitOverrideSwitch } from "./AddOnLimitOverrideSwitch";
import { CouponValidationModal } from "./CouponValidationModal";
import { PaymentBreakdown, PriceStrategy } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "./PurchaseItemSummaryTile";
import { api } from "@/api/api";
import { findValue } from "@/api/interceptors";
import { useSeller } from "@/hooks/useAccount";
import { OverrideCatalogItemRestrictions, useAddOnLimitOverride } from "@/hooks/useAddOnLimitOverride";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { usePackage } from "@/hooks/usePackage";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";
import { getStart } from "@/models/Event";
import { hasTimeScheduled, isVirtualExperience } from "@/models/Experience";
import { isExperienceExistsInPackage } from "@/models/Package";
import { getItemById } from "@/models/Purchase";
import {
    getCouponLineItems,
    getFirstAvailableExperiencePurchaseItem,
    getTimeslotIdFromPurchaseItem,
    isAllowedPrivacy,
    isPriceConfigurationUpdated,
} from "@/models/PurchaseItem";
import { shouldRetainPriceByDefault } from "@/models/Seller";
import { getPurchaseItemPrivacyForTimeSlot } from "@/models/Timeslot";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { isTableView } from "@/stores/dashboardFilterStore";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";
import { updateCache } from "@/utils/updateCache";

export type OnChangeArrivalContinueHandler = (previousEvent: Event, newPurchase?: Purchase) => void;

interface PurchaseChangeArrivalModalProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isOpen: boolean;
    onClose: (newPurchase?: Purchase, redirectUrl?: string, refundedItem?: PurchaseItem) => void;

    /** Called when the previous event had guides assigned and needs additional processing */
    onContinue?: OnChangeArrivalContinueHandler;
}

interface UpdatePurchasePayload {
    items: Array<
        {
            id: ID;
            arrivalDate: string;
            arrivalTime?: number | null;
            _priceStrategy?: PriceStrategy;
        } & OverrideCatalogItemRestrictions
    >;
}

function getArrivalTime(newArrivalTime: null | number, oldArrivalTime: undefined | number, isAllDay: boolean) {
    if (isAllDay && isNumber(oldArrivalTime)) {
        // schedule was time based during reservation, but now is all-day based,
        // the arrivalTime must be passed as null so that the API unsets the arrivalTime.
        return null;
    }

    return newArrivalTime ?? undefined;
}

export const PurchaseChangeArrivalModal = ({
    purchase,
    item,
    isOpen,
    onClose,
    onContinue,
}: PurchaseChangeArrivalModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent purchase={purchase} item={item} onContinue={onContinue} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    onClose: (newPurchase?: Purchase, redirectUrl?: string, refundedItem?: PurchaseItem) => void;
    onContinue?: OnChangeArrivalContinueHandler;
}

const isItemScheduled = (item: PurchaseItem) =>
    item.object === PurchaseItemObject.EXPERIENCE || item.object === PurchaseItemObject.EQUIPMENT;

const ModalContent = ({ purchase, item, onClose, onContinue }: ModalContentProps) => {
    useAuthorization();
    const seller = useSeller();
    const [visible, { show: showRestrictionModal, hide: hideRestrictionModal }] = useVisibility(false);

    const [selectedItem, setSelectedItem] = useState<ExperiencePurchaseItem>(
        getFirstAvailableExperiencePurchaseItem(purchase, item),
    );
    const invalidate = useInvalidate();

    const eventId = useMemo(
        () => {
            return selectedItem?.event.id;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItem?.id],
    );

    const { data: experience, isLoading: isExperienceLoading } = useResource<Experience>([
        "products",
        selectedItem.product.id,
    ]);

    const shouldShowPrivacyWarning =
        experience?.allowedPrivacies &&
        isAllowedPrivacy(selectedItem.privacy) &&
        !experience.allowedPrivacies.includes(selectedItem.privacy);

    const supportedPrivacy = experience?.allowedPrivacies?.[0];
    const { data: event } = useResource<Event>(
        ["events", eventId],
        {},
        { enabled: !isOtherSellerPurchase(purchase, seller.id) },
    );

    const allowOverlap =
        seller.roles.includes(FeatureRole.SELLER_INVENTORY) && experience && experience.resources.length > 0;

    const initialValues: Arrival = { date: "", time: null };

    const packageId = item.parentProduct?.id;
    const { pkg } = usePackage(packageId, { isPartnerPackage: isPartnerPackagePurchase(purchase) });
    const isExperienceRemovedFromPackage =
        packageId && pkg && !isExperienceExistsInPackage(pkg, selectedItem?.product?.id);

    const [arrival, setArrival] = useState<Arrival>(initialValues);
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());
    const [notify, setNotify] = useState(true);
    const shouldIgnoreRestrictions = useCreatePurchaseStore((state) => state.shouldIgnoreRestrictions);
    const addOnLimitOverride = useAddOnLimitOverride();
    const [error, setError] = useState<string>("");
    const [arrivalRestrictionMessage, setArrivalRestrictionMessage] = useState<string>();
    const [ignoredRestriction, setIgnoredRestriction] = useState<string>("");
    const [nextItem, setNextItem] = useState<ExperiencePurchaseItem | null>(null);
    const [isPromptVisible, { show: showPrompt, hide: hidePrompt }] = useVisibility(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const setDrawerClose = useRosterDrawerStore((state) => state.setClose);

    const arrivalTimezoneName = isVirtualExperience(experience) ? seller.timezoneName : undefined;

    const history = useHistory();
    const isDashboardPage =
        useMemo(() => history.location.pathname.startsWith("/dashboard"), [history]) && !isTableView();

    useEffect(() => {
        setArrival({ date: "", time: null });
    }, [selectedItem]);

    const { mutateAsync: changeArrival, isSuccess } = useMutation(async (payload: UpdatePurchasePayload) => {
        const params: { validationLevel?: string } = {};
        if (shouldIgnoreRestrictions) {
            params.validationLevel = "warning";
        }

        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload, { params: params });
        return data;
    });

    const handleArrivalChange = (value: Arrival) => {
        useCreatePurchaseStore.setState({ shouldIgnoreRestrictions: false });
        setArrival(value);
        setIgnoredRestriction("");
        addOnLimitOverride.reset();
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const updatedPurchase = await changeArrival(
                getPayload({
                    item: selectedItem,
                    arrivalDate: arrival.date,
                    showRetainPriceToggle,
                    retainPrice,
                    notify,
                    arrivalTime: getArrivalTime(arrival.time, selectedItem.arrivalTime, !isTimeRequired),
                    itemOverride: addOnLimitOverride.itemOverride,
                }),
            );

            const updatedPurchaseItem = getItemById(updatedPurchase, item.id);
            const [updatedEvent, previousEvent] = await fetchUpdatedEvents(
                purchase,
                seller.id,
                updatedPurchaseItem,
                eventId,
                event,
            );

            // Url uses for redirecting on submit for roster view
            const oldTimeslotId = getTimeslotIdFromPurchaseItem(item);
            const timeslotId = getTimeslotIdFromPurchaseItem(updatedPurchaseItem);
            const url = `/dashboard/${timeslotId}`;

            setDrawerClose();
            updateCache("purchases", updatedPurchase);
            updateCache("purchaseItems", updatedPurchase.items);

            if (isDashboardPage) {
                invalidate("timeslots", oldTimeslotId);
                if (updatedEvent) {
                    invalidate("timeslots", timeslotId);
                }
            }

            handleSuccessfulSubmission(() => {
                if (previousEvent && (previousEvent?.guides?.length ?? 0) > 0) {
                    onContinue?.(previousEvent, updatedPurchase);
                } else {
                    onClose(updatedPurchase, url, selectedItem);
                }
            });
        } catch (error: any) {
            if (addOnLimitOverride.handleError(error)) {
                return;
            }

            if (error?.response?.data?.field) {
                flash.danger(`Error changing arrival. There are ${error?.response?.data?.field.open} slots available`);
            }

            const arrivalError = findValue(getArrivalErrors(error)?.[0], "message");
            if (arrivalError) {
                flash.danger(arrivalError);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isExperienceLoading || !experience) {
        return <Loader />;
    }

    const isTimeRequired = !isAllDay(experience);

    const isSameTimeslot = isTimeRequired
        ? arrival.date === selectedItem.arrivalDate && arrival.time === selectedItem.arrivalTime
        : arrival.date === selectedItem.arrivalDate;

    const isDateEmpty = !arrival.date;
    const areChangesSaved = isDateEmpty || isSameTimeslot || (isTimeRequired && arrival.time === null);

    const items = purchase.items.filter((item) => {
        return isNonCanceledExperienceItem(item as ExperiencePurchaseItem) && isItemScheduled(item);
    });

    const shouldShowError = !isEmpty(error) || !isEmpty(ignoredRestriction);

    const handlePaymentBreakdownError = (error: any) => {
        const arrivalErrors = getArrivalErrors(error);
        const hasCouponLineItems = getCouponLineItems(item).length > 0;
        const arrivalErrorObject = findValue(arrivalErrors?.[0], "object");

        if (hasCouponLineItems && arrivalErrorObject === PurchaseRuleFilterError.VALIDATION_ERROR) {
            setArrivalRestrictionMessage(findValue(arrivalErrors?.[0], "message") ?? undefined);
            showRestrictionModal();
        } else if (isEmpty(arrivalErrors)) {
            flash.danger(findValue(error?.response?.data, "message") ?? "Error fetching purchase information");
        } else {
            setError(findValue(arrivalErrors?.[0], "message"));
        }
    };

    const handlePaymentBreakdownUpdate = (purchase: Purchase | null) => {
        if (isPriceConfigurationUpdated(purchase, item) && !showRetainPriceToggle) {
            setShowRetainPriceToggle(true);
            setRetainPrice(!retainPrice);
        }

        setError("");
    };

    const isPackageBooking = isPackageItem(item);

    const selectItem = (item: ExperiencePurchaseItem) => {
        addOnLimitOverride.reset();
        setSelectedItem(item);
    };

    const handleItemChange = (item: PurchaseItem) => {
        if (!areChangesSaved) {
            setNextItem(item as ExperiencePurchaseItem);
            showPrompt();
            return;
        }

        selectItem(item as ExperiencePurchaseItem);
    };

    const handleConfirmLeave = () => {
        setNextItem(null);
        hidePrompt();
        if (nextItem) {
            selectItem(nextItem);
        }
    };

    const handleCancelLeave = () => {
        setNextItem(null);
        hidePrompt();
    };

    const handleIgnoreRestrictionsChange = (isChecked: boolean) => {
        useCreatePurchaseStore.setState({ shouldIgnoreRestrictions: isChecked });

        if (isChecked && error) {
            setIgnoredRestriction(error);
        } else {
            setIgnoredRestriction("");
        }
    };

    return (
        <fieldset disabled={isLoading}>
            <ConfirmLeaveModal
                disableRouterPrompt
                copy="You have unsaved changes on this screen. Navigating away will cause you to lose these changes. Are you sure you want to continue?"
                manualShowPrompt={isPromptVisible}
                handleConfirm={handleConfirmLeave}
                handleCancel={handleCancelLeave}
            />
            <Modal.Header>Change Arrival</Modal.Header>

            <Modal.Body className="min-h-30 flex-col">
                {items.length > 1 && (
                    <>
                        <Label>Select Reservation</Label>
                        <div className="mb-4 w-full">
                            <PurchaseItemSelect
                                items={items}
                                value={selectedItem}
                                className="flex-1"
                                onChange={handleItemChange}
                            />
                        </div>
                    </>
                )}
                <div className="flex space-x-4">
                    <div className="flex-1 space-y-4">
                        <PurchaseItemSummaryTile purchase={purchase} item={selectedItem} />
                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />
                        {isExperienceRemovedFromPackage ? (
                            <PackageExperienceRemovedAlert />
                        ) : (
                            <ArrivalPicker
                                showCapacity
                                autoSelectTimeslot={false}
                                timezone={arrivalTimezoneName}
                                quantity={selectedItem.quantity}
                                value={arrival}
                                showAddNewTimeSlot={hasTimeScheduled(experience)}
                                product={selectedItem.product.id}
                                placement="right"
                                item={item}
                                allowOverlap={allowOverlap}
                                privacyPref={getPurchaseItemPrivacyForTimeSlot(
                                    selectedItem.privacy,
                                    experience?.allowedPrivacies,
                                )}
                                packageId={item.parentProduct?.id}
                                onChange={handleArrivalChange}
                            />
                        )}
                        {shouldShowError && (
                            <div className="flex w-full flex-col">
                                <span className="text-danger">
                                    {shouldIgnoreRestrictions ? ignoredRestriction : error}
                                </span>

                                {isPackageBooking && (
                                    <div className="mt-2 self-start">
                                        <SwitchField
                                            size="small"
                                            isChecked={shouldIgnoreRestrictions}
                                            onChange={handleIgnoreRestrictionsChange}
                                        >
                                            Ignore Restrictions
                                        </SwitchField>
                                    </div>
                                )}
                            </div>
                        )}
                        <AddOnLimitOverrideSwitch limitOverride={addOnLimitOverride} />
                        {shouldShowPrivacyWarning && (
                            <div className="flex w-full flex-col">
                                <Alert color="warning" className="text-left">
                                    This experience no longer supports {selectedItem.privacy} pricing. Please change the
                                    privacy for this reservation to {supportedPrivacy} if you would like the latest
                                    pricing to apply.
                                </Alert>
                            </div>
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="rounded bg-gray-lighter p-8">
                            <PaymentBreakdown
                                purchase={{ ...purchase }}
                                item={selectedItem}
                                isEnabled={!!arrival.date}
                                validationLevel={shouldIgnoreRestrictions ? ValidationLevel.WARNING : ""}
                                payload={getPayload({
                                    item: selectedItem,
                                    arrivalDate: arrival.date,
                                    showRetainPriceToggle,
                                    retainPrice,
                                    notify,
                                    arrivalTime: arrival.time ?? undefined,
                                })}
                                onUpdate={handlePaymentBreakdownUpdate}
                                onError={handlePaymentBreakdownError}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <fieldset disabled={isLoading} className="flex justify-end space-x-4">
                    <NotifyCustomerCheckbox isChecked={notify} onChange={(e: any) => setNotify(e.target.checked)} />

                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                    <SubmitButton
                        isLoading={isLoading}
                        isSuccess={isSuccess && !isLoading}
                        disabled={areChangesSaved || !isEmpty(error) || addOnLimitOverride.isAddonLimitExceeded}
                        onClick={handleSubmit}
                    >
                        Save Changes
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
            <CouponValidationModal
                isOpen={visible}
                item={item}
                arrivalRestrictionMessage={arrivalRestrictionMessage}
                onClose={hideRestrictionModal}
                onSubmit={hideRestrictionModal}
            />
        </fieldset>
    );
};

/** Fetches updated and previous events after a successful arrival change. Extracted to reduce ModalContent complexity. */
async function fetchUpdatedEvents(
    purchase: Purchase,
    sellerId: string,
    updatedPurchaseItem: PurchaseItem,
    eventId: string,
    currentEvent: Event | undefined,
): Promise<[Event | null, Event | null]> {
    if (isOtherSellerPurchase(purchase, sellerId)) {
        return [null, null];
    }

    const { data: updatedEvent } = await api.get<Event>(`/events/${updatedPurchaseItem.event.id}`);

    // Sometimes the previous event gets deleted from the backend like for MPE. We fetch it to check for guides.
    try {
        const { data: previousEvent } = await api.get<Event>(`/events/${eventId}`);
        return [updatedEvent, previousEvent];
    } catch {
        if (currentEvent && !isEmpty(currentEvent.guides)) {
            const eventDate = formatDate(getStart(currentEvent), DateFormat.DATE_TIME_SHORT);
            if (isEmpty(currentEvent.guides)) {
                flash.success(`The guides assigned to the previous event at ${eventDate} have been removed`);
            } else {
                flash.success(`The previous event at ${eventDate} has been removed.`);
            }
        }
        return [updatedEvent, null];
    }
}

interface ArrivalChangePayloadParams {
    item: PurchaseItem;
    arrivalDate: string;
    showRetainPriceToggle: boolean;
    retainPrice: boolean;
    notify: boolean;
    arrivalTime: number | null | undefined;
    arrivalTimezoneName?: string;
    itemOverride?: OverrideCatalogItemRestrictions;
}

const getPayload = ({
    item,
    arrivalDate,
    showRetainPriceToggle,
    retainPrice,
    notify,
    arrivalTime,
    arrivalTimezoneName,
    itemOverride = {},
}: ArrivalChangePayloadParams) => {
    if (showRetainPriceToggle && retainPrice) {
        return {
            items: [
                {
                    id: item.id,
                    arrivalDate,
                    arrivalTime,
                    arrivalTimezoneName,
                    _priceStrategy: PriceStrategy.RETAIN,
                    ...itemOverride,
                },
            ],
            _emailOverrides: { sendEmailToTraveler: notify },
        };
    }

    return {
        items: [{ id: item.id, arrivalDate, arrivalTime, arrivalTimezoneName, ...itemOverride }],
        _emailOverrides: { sendEmailToTraveler: notify },
    };
};

export const PackageExperienceRemovedAlert = () => {
    return (
        <Alert color="warning" className="mt-2 mb-2 py-4 px-6">
            <p>Your change cannot be completed because this Listing has been removed from this Package.</p>
            <p>To make this change, you must cancel this purchase and place a new booking with your desired changes.</p>
        </Alert>
    );
};

const getArrivalErrors = (error: any) => {
    return error?.response?.data?.errors?.filter((error: ArrivalError) => error.field === "arrivalDateTime") ?? [];
};
